import SuccessIcon from './assets/data-upload-success.svg';
import WarningIcon from './assets/data-upload-warning-amber.svg';
import { FlatUploadRequirementsData } from '../interfaces/UploadRequirementsInterface';
import { PossibleUploadStates } from '../interfaces/UploadRequirementsInterface';
import CustomDivider from './reusable/CustomDivider';
import { AxiosResponse } from 'axios';
import LoaderIcon from './assets/data-upload-loader.svg';
import ExternalSchoolTag from './reusable/ExternalSchoolTag';
import useSchools from './hooks/useSchools';

import './SchoolsList.css';

export type SchoolsListData = PromiseSettledResult<AxiosResponse<FlatUploadRequirementsData>>;
interface SchoolsListProps {
  data: SchoolsListData;
}

export function SchoolsList({ data }: SchoolsListProps) {
  const {
    schools: { data: schools },
  } = useSchools();

  if (data.status === 'rejected') return <p className="center-text">Missing upload requirements data!</p>;

  let schoolsData = data.value.data.schools;

  const isExternalSchool = (schoolName: string): boolean => {
    const school = schools?.find((s) => s.name === schoolName);
    return school?.is_primary_on_eoa === false;
  };

  const prepareData = () => {
    const sections = [
      {
        requirementMet: PossibleUploadStates.UPLOAD_IN_PROGRESS,
        title: 'Data processing',
        schools: [] as [string, boolean | string][],
        icon: LoaderIcon,
        iconAltText: 'processing icon',
      },
      {
        requirementMet: PossibleUploadStates.NOT_YET_UPLOADED,
        title: 'Not yet started',
        schools: [] as [string, boolean | string][],
        icon: null,
        iconAltText: '',
      },
      {
        requirementMet: PossibleUploadStates.UPLOADED_WITH_ERRORS,
        title: 'Contains errors',
        schools: [] as [string, boolean | string][],
        icon: WarningIcon,
        iconAltText: 'warning icon',
      },
      {
        requirementMet: PossibleUploadStates.UPLOADED,
        title: 'Completed',
        schools: [] as [string, boolean | string][],
        icon: SuccessIcon,
        iconAltText: 'check mark icon',
      },
    ];

    Object.entries(schoolsData).forEach(([schoolName, requirementMet]) => {
      if (!isExternalSchool(schoolName)) {
        const section = sections.find((s) => s.requirementMet === requirementMet);
        if (section) section.schools.push([schoolName, requirementMet]);
      }
    });

    return sections;
  };

  const sections = prepareData();

  return (
    <>
      {sections.map((section, index) => (
        <div key={index}>
          <CustomDivider orientation="horizontal" size="medium" />
          <SchoolsWithStatus
            title={section.title}
            schools={section.schools}
            icon={section.icon}
            iconAltText={section.iconAltText}
          />
        </div>
      ))}
    </>
  );
}

interface SchoolsWithStatusProps {
  title: string;
  schools: [string, boolean | string][];
  icon?: string | null;
  iconAltText: string;
}

function SchoolsWithStatus({ title, schools, icon, iconAltText }: SchoolsWithStatusProps) {
  const {
    schools: { data: schoolsData },
  } = useSchools();

  const isExternalSchool = (schoolName: string): boolean => {
    const school = schoolsData?.find((s) => s.name === schoolName);
    return school?.is_primary_on_eoa === false;
  };

  return (
    <>
      <div className="data-status">
        <p>{title}</p>
        <span>{`${schools.length} schools`}</span>
      </div>
      {schools.map(([schoolName], index) => (
        <div key={index} className="school-container">
          {icon ? <img className="icon-size" src={icon} alt={iconAltText} /> : null}
          <span>
            {schoolName}
            {isExternalSchool(schoolName) && <ExternalSchoolTag variant="ext" />}
          </span>
        </div>
      ))}
    </>
  );
}

export default SchoolsList;
